<template>
  <div class="farm-manager">
    <!-- Searchable farm list — reuses the precompiled farms.geojson the
         FarmsMap already loads, so no extra full-collection read. -->
    <input
      v-model="search"
      class="fm-search"
      type="text"
      placeholder="Search farms by name or ID…"
    />

    <p v-if="loadingFarms" class="text-muted">
      <i class="fas fa-spinner fa-spin"></i> Loading farms…
    </p>
    <p v-else-if="farmsError" class="text-warning">{{ farmsError }}</p>

    <ul v-else class="fm-list">
      <li
        v-for="f in filteredFarms"
        :key="f.id"
        class="fm-item"
        :class="{ active: selected && selected.id === f.id }"
        @click="selectFarm(f)"
      >
        <span class="fm-name">{{ f.name }}</span>
        <span class="fm-id">{{ f.id }}</span>
      </li>
      <li v-if="!filteredFarms.length" class="fm-empty">No farms match “{{ search }}”.</li>
    </ul>

    <!-- Farm detail — editable options. AVIRIS is the only flag for now;
         this panel is the seam where future per-farm admin controls land. -->
    <div v-if="selected" class="fm-detail">
      <h3>{{ selected.name }}</h3>
      <p class="fm-id mb-2">{{ selected.id }}</p>

      <p v-if="loadingDetail" class="text-muted">
        <i class="fas fa-spinner fa-spin"></i> Loading farm…
      </p>
      <p v-else-if="detailError" class="text-warning">{{ detailError }}</p>

      <template v-else-if="farmDoc">
        <div class="fm-flag">
          <label class="fm-flag-label">
            <input
              type="checkbox"
              v-model="avirisEnabled"
              :disabled="saving"
              @change="saveAviris"
            />
            <span>AVIRIS-3 hyperspectral access</span>
          </label>
          <span v-if="saving" class="fm-note">saving…</span>
          <span v-else-if="savedNote" class="fm-note saved">{{ savedNote }}</span>
        </div>
        <p class="fm-hint">
          When on, this farm sees the AVIRIS-3 hyperspectral source in the
          satellite plugin (where flight coverage exists).
        </p>
        <div class="fm-flag">
          <label class="fm-flag-label">
            <input
              type="checkbox"
              v-model="missionsEnabled"
              :disabled="saving"
              @change="saveMissions"
            />
            <span>Mission Dispatch plugin</span>
          </label>
          <span v-if="saving" class="fm-note">saving…</span>
          <span v-else-if="missionsSavedNote" class="fm-note saved">{{ missionsSavedNote }}</span>
        </div>
        <p class="fm-hint">
          When on, this farm's Editors/Admins see the Missions plugin (robot
          dispatch — docs/mission-dispatch-v1.md). Restricted: never appears
          in the self-serve plugin library; only this toggle grants it.
        </p>

        <!-- Launch the farm as a simulated role for support / QA. -->
        <div class="fm-launch">
          <p class="fm-launch-label">Launch as</p>
          <div class="fm-launch-row">
            <select v-model="launchRole" class="fm-role-select">
              <option v-for="r in launchRoles" :key="r" :value="r">{{ r }}</option>
            </select>
            <button class="fm-launch-btn" @click="launchAs">
              <i class="fas fa-external-link-alt"></i> Launch farm
            </button>
          </div>
          <p class="fm-hint">
            Opens this farm with the interface gated as the chosen role.
            You remain a super-admin on the backend — this simulates the
            <strong>UI experience</strong>, not database permissions.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app'
const fb = require('../../firebaseConfig.js')

export default {
  name: 'FarmManager',
  data () {
    return {
      search: '',
      farms: [],            // [{ id, name }] from the precompiled geojson
      loadingFarms: true,
      farmsError: null,
      selected: null,       // the { id, name } the admin clicked
      farmDoc: null,        // full Firestore farm document
      loadingDetail: false,
      detailError: null,
      avirisEnabled: false,
      missionsEnabled: false,
      missionsSavedNote: '',
      saving: false,
      savedNote: '',
      launchRole: 'Administrator',
      launchRoles: ['Owner', 'Administrator', 'Editor', 'Read-only', 'Permission-only']
    }
  },
  computed: {
    filteredFarms () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.farms
      return this.farms.filter(f =>
        f.name.toLowerCase().includes(q) || f.id.toLowerCase().includes(q)
      )
    }
  },
  created () {
    this.loadFarms()
  },
  methods: {
    loadFarms () {
      this.loadingFarms = true
      this.farmsError = null
      const ref = firebase.storage().ref('statistics/farms/farms.geojson')
      ref.getDownloadURL()
        .then(url => fetch(url))
        .then(r => r.json())
        .then(geojson => {
          this.farms = (geojson.features || [])
            .map(f => ({
              id: f.properties.id,
              name: f.properties.name || '(unnamed farm)'
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        })
        .catch(err => {
          this.farmsError = 'Could not load the farms list: ' + (err.message || err)
        })
        .finally(() => { this.loadingFarms = false })
    },
    selectFarm (f) {
      this.selected = f
      this.farmDoc = null
      this.detailError = null
      this.savedNote = ''
      this.loadingDetail = true
      fb.farmsCollection.doc(f.id).get()
        .then(snap => {
          if (!snap.exists) {
            this.detailError = 'Farm document not found.'
            return
          }
          this.farmDoc = snap.data()
          // features may not exist yet on older farm docs
          this.avirisEnabled = !!(this.farmDoc.features && this.farmDoc.features.aviris)
          this.missionsEnabled = !!(this.farmDoc.plugins && this.farmDoc.plugins.includes('missions'))
        })
        .catch(err => {
          this.detailError = 'Could not load farm: ' + (err.message || err)
        })
        .finally(() => { this.loadingDetail = false })
    },
    saveAviris () {
      if (!this.selected) return
      this.saving = true
      this.savedNote = ''
      // Dot-path update so we set just this flag without clobbering any
      // other keys a future `features` map might hold.
      fb.farmsCollection.doc(this.selected.id).update({
        'features.aviris': this.avirisEnabled,
        updatedBy: this.$store.state.currentUser && this.$store.state.currentUser.uid,
        updatedOn: new Date()
      })
        .then(() => {
          this.savedNote = this.avirisEnabled ? 'enabled' : 'disabled'
        })
        .catch(err => {
          // revert the checkbox so the UI reflects the real stored state
          this.avirisEnabled = !this.avirisEnabled
          this.detailError = 'Save failed: ' + (err.message || err)
        })
        .finally(() => { this.saving = false })
    },
    saveMissions () {
      if (!this.selected) return
      this.saving = true
      this.missionsSavedNote = ''
      // arrayUnion/arrayRemove so we never clobber other entries in the
      // farm's plugins array (which self-serve library activations also use).
      const op = this.missionsEnabled
        ? firebase.firestore.FieldValue.arrayUnion('missions')
        : firebase.firestore.FieldValue.arrayRemove('missions')
      fb.farmsCollection.doc(this.selected.id).update({
        plugins: op,
        updatedBy: this.$store.state.currentUser && this.$store.state.currentUser.uid,
        updatedOn: new Date()
      })
        .then(() => {
          this.missionsSavedNote = this.missionsEnabled ? 'enabled' : 'disabled'
        })
        .catch(err => {
          this.missionsEnabled = !this.missionsEnabled
          this.detailError = 'Save failed: ' + (err.message || err)
        })
        .finally(() => { this.saving = false })
    },
    launchAs () {
      if (!this.selected) return
      // Record the simulation for the immediate banner, then open the farm
      // with the role in the URL (?simAs=) so it survives reloads / new tabs /
      // direct links — Farm.vue rehydrates state.simulation from that query.
      this.$store.commit('setStateProperty', {
        property: 'simulation',
        value: {
          active: true,
          role: this.launchRole,
          farmId: this.selected.id,
          farmName: this.selected.name
        }
      })
      this.$router.push('/f/' + this.selected.id + '?simAs=' + encodeURIComponent(this.launchRole))
    }
  }
}
</script>

<style scoped>
.fm-search {
  width: 100%;
  background: #1f262e;
  border: 1px solid #38404b;
  border-radius: 6px;
  color: #eee;
  padding: 8px 10px;
  font-size: 0.9rem;
  margin-bottom: 10px;
}
.fm-search::placeholder { color: #667; }

.fm-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #38404b;
  border-radius: 6px;
}
.fm-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 7px 12px;
  border-bottom: 1px solid #2f3742;
  cursor: pointer;
}
.fm-item:last-child { border-bottom: none; }
.fm-item:hover { background: #1f262e; }
.fm-item.active { background: #1f262e; box-shadow: inset 3px 0 0 #ffc107; }
.fm-name { color: #eee; font-size: 0.88rem; }
.fm-id { color: #667; font-size: 0.72rem; font-variant-numeric: tabular-nums; }
.fm-empty { padding: 10px 12px; color: #889; font-size: 0.85rem; }

.fm-detail {
  margin-top: 14px;
  background: #1f262e;
  border: 1px solid #38404b;
  border-radius: 6px;
  padding: 14px 16px;
}
.fm-detail h3 { font-size: 1rem; color: #eee; margin: 0; }

.fm-flag {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}
.fm-flag-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #eee;
  font-size: 0.9rem;
  cursor: pointer;
}
.fm-note { font-size: 0.78rem; color: #889; }
.fm-note.saved { color: #4caf50; }
.fm-hint { color: #889; font-size: 0.78rem; margin: 6px 0 0; }

.fm-launch {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #38404b;
}
.fm-launch-label {
  color: #eee;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 6px;
}
.fm-launch-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fm-role-select {
  background: #11161b;
  border: 1px solid #38404b;
  border-radius: 6px;
  color: #eee;
  padding: 7px 10px;
  font-size: 0.85rem;
  flex: 0 0 auto;
}
.fm-launch-btn {
  background: #ffc107;
  border: none;
  border-radius: 6px;
  color: #1f262e;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 7px 14px;
  cursor: pointer;
}
.fm-launch-btn:hover { background: #ffcd38; }

.text-muted { color: #889 !important; }
.text-warning { color: #ffc107 !important; }
</style>
